<template>
  <!-- « Premiers pas » : mise en route de la boutique active, étapes cochées d'après les données -->
  <teleport to="body">
    <div v-if="state.open" class="modal d-block onboarding-modal" tabindex="-1" @click.self="close">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div class="modal-content">
          <div class="modal-header align-items-start">
            <div>
              <h3 class="modal-title fs-3 fw-bolder">
                <i class="bi bi-signpost-split text-primary me-2"></i>{{ $t('onboarding.title') }}
              </h3>
              <div class="text-muted fs-7 mt-1">{{ $t('onboarding.subtitle') }}</div>
            </div>
            <button type="button" class="btn btn-sm btn-icon btn-light" :aria-label="$t('button.close')" @click="close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="modal-body">
            <div v-if="!progress" class="text-center py-10">
              <span class="spinner-border spinner-border-sm me-2"></span>{{ $t('loading') }}
            </div>
            <template v-else>
              <div class="d-flex align-items-center gap-3 mb-2">
                <span class="fw-bold text-gray-800">{{ $t('onboarding.progress', { done: doneCount, total: steps.length }) }}</span>
                <span v-if="doneCount === steps.length" class="badge badge-light-success">{{ $t('onboarding.all_done') }}</span>
              </div>
              <div class="progress h-6px mb-6">
                <div class="progress-bar bg-primary" :style="{ width: percent + '%' }"></div>
              </div>

              <ol class="onboarding-steps">
                <li v-for="(step, index) in steps" :key="step.key"
                    class="onboarding-step"
                    :class="{ 'is-done': step.done, 'is-next': index === nextIndex, 'is-locked': step.locked }">
                  <span class="onboarding-step__mark">
                    <i v-if="step.done" class="bi bi-check-lg"></i>
                    <span v-else>{{ index + 1 }}</span>
                  </span>
                  <div class="onboarding-step__text">
                    <div class="onboarding-step__title">{{ $t(step.title) }}</div>
                    <div class="onboarding-step__hint">{{ step.locked ? $t('onboarding.locked', { step: $t(stepTitle(step.lockedBy)) }) : $t(step.hint) }}</div>
                  </div>
                  <div v-if="!step.locked" class="onboarding-step__actions">
                    <button type="button" class="btn btn-sm"
                            :class="step.done ? 'btn-light' : (index === nextIndex ? 'btn-primary' : 'btn-light-primary')"
                            @click="go(step.to)">
                      {{ step.done ? $t('onboarding.open') : $t('onboarding.do') }}
                    </button>
                  </div>
                </li>
              </ol>

              <!-- facultatif : ne compte pas dans la progression -->
              <div v-if="$can('users_view')" class="onboarding-extra">
                <i class="bi bi-people text-muted"></i>
                <span>{{ $t('onboarding.team') }}</span>
                <button type="button" class="btn btn-sm btn-link ms-auto p-0" @click="go('/admin/settings-user')">{{ $t('onboarding.team_cta') }}</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
/**
 * Fenêtre « Premiers pas » (gérant), ouverte depuis l'aide ou le tableau de bord. Parcours : infos de la boutique,
 * catégories et sous-catégories, articles dans les sous-catégories, articles mis en vente
 * dans la boutique (prix, et stock si le module Stock est actif), première vente ; puis avec
 * l'e-commerce : livraison, paiement en ligne, accueil. Chaque étape ne s'ouvre qu'une fois
 * la précédente faite ; « Faire » ferme la fenêtre et ouvre l'écran de l'étape.
 */
import { onboardingState, closeOnboarding } from '@/onboarding/onboardingState'

export default {
  name: 'OnboardingModal',
  data() {
    return { state: onboardingState }
  },
  computed: {
    progress() {
      return this.state.progress
    },
    shopId() {
      return this.progress?.shop_id
    },
    steps() {
      if (!this.progress) return []
      const shop = { id: this.shopId }
      const list = [
        { key: 'infos', title: 'onboarding.steps.infos', hint: 'onboarding.hints.infos', to: { name: 'shop-detail-products', params: shop } },
        { key: 'categories', title: 'onboarding.steps.categories', hint: 'onboarding.hints.categories', to: { name: 'settings-categories' } },
        { key: 'products', title: 'onboarding.steps.products', hint: 'onboarding.hints.products', to: { name: 'settings-items' } },
        {
          key: 'shop_products',
          title: 'onboarding.steps.shop_products',
          hint: this.progress.with_stock ? 'onboarding.hints.shop_products_stock' : 'onboarding.hints.shop_products',
          to: { name: 'shop-add-products', params: shop },
        },
        { key: 'first_sale', title: 'onboarding.steps.first_sale', hint: 'onboarding.hints.first_sale', to: { name: 'pos-caisse-path' } },
      ]
      if (this.progress.with_ecommerce) {
        list.push(
          { key: 'delivery', title: 'onboarding.steps.delivery', hint: 'onboarding.hints.delivery', to: { name: 'setting-shipping-areas' } },
          { key: 'online_payment', title: 'onboarding.steps.online_payment', hint: 'onboarding.hints.online_payment', to: { name: 'setting-system-payment' } },
          { key: 'homepage', title: 'onboarding.steps.homepage', hint: 'onboarding.hints.homepage', to: { name: 'shop-media-list', params: shop } },
        )
      }
      // une étape ne s'ouvre que lorsque celle dont elle dépend est faite : la précédente, sauf
      // pour l'e-commerce, qui suit la mise en vente dans la boutique (une boutique qui ne vend
      // qu'en ligne n'attend pas une vente en caisse)
      const dependsOn = { delivery: 'shop_products' }
      return list.map((step, index) => {
        const previous = dependsOn[step.key] ?? (index > 0 ? list[index - 1].key : null)
        const done = !!this.progress.steps[step.key]
        return {
          ...step,
          done,
          lockedBy: previous,
          locked: !!previous && !this.progress.steps[previous] && !done,
        }
      })
    },
    doneCount() {
      return this.steps.filter(step => step.done).length
    },
    percent() {
      return this.steps.length ? Math.round((this.doneCount / this.steps.length) * 100) : 0
    },
    nextIndex() {
      return this.steps.findIndex(step => !step.done)
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    stepTitle(key) {
      return this.steps.find(step => step.key === key)?.title || ''
    },
    close() {
      closeOnboarding()
    },
    go(to) {
      closeOnboarding()
      this.$router.push(to)
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.state.open) closeOnboarding()
    },
  },
}
</script>

<style scoped>
.onboarding-modal { z-index: 1065; background: rgba(0, 0, 0, 0.35); }
.onboarding-steps { list-style: none; margin: 0; padding: 0; }
.onboarding-step {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-top: 1px dashed #eff2f5;
}
.onboarding-step:first-child { border-top: 0; padding-top: 0; }
.onboarding-step__mark {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f3f8;
  color: #7e8299;
  font-weight: 700;
  font-size: 0.85rem;
}
.onboarding-step.is-next .onboarding-step__mark { background: var(--bs-primary, #3699ff); color: #fff; }
.onboarding-step.is-done .onboarding-step__mark { background: #e8fff3; color: #50cd89; font-size: 1.1rem; }
.onboarding-step__text { flex: 1; min-width: 0; }
.onboarding-step__title { font-weight: 600; color: #181c32; }
.onboarding-step.is-done .onboarding-step__title { color: #a1a5b7; text-decoration: line-through; }
.onboarding-step.is-locked .onboarding-step__title { color: #a1a5b7; }
.onboarding-step__hint { font-size: 0.85rem; color: #7e8299; margin-top: 2px; }
.onboarding-step__actions { display: flex; gap: 8px; flex-shrink: 0; }
.onboarding-extra {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #eff2f5;
  font-size: 0.9rem;
  color: #7e8299;
}
@media (max-width: 575px) {
  .onboarding-step { flex-wrap: wrap; }
  .onboarding-step__actions { width: 100%; padding-left: 44px; }
}
</style>
